import { useState } from 'react';
import { Banner, FormCheckbox, FormHelpText, Image, PaginationControls, Type } from '@spotify-internal/encore-web';
import { clamp, filter, isEqual, negate, partial } from 'lodash';
import { useTranslation } from 'react-i18next';

import { paginate } from 'utils/functions';

import styles from './ImageChooser.module.scss';

const IMAGES_PER_PAGE = 4;

type ImageChooserProps<T> = {
  label: string;
  field: { name: string; required?: boolean };
  valid: boolean;
  images: T[];
  imageProperty: keyof T;
  value: T[];
  onFieldChange: (fieldChange: { fieldIdentifier: string; value: T[]; valid: boolean }) => void;
  max?: number;
  translations: {
    blurWarning: string;
    disclaimer?: string;
    pagination: string;
    errorMessage: string;
  };
};

const ImageChooser = <T,>(props: ImageChooserProps<T>) => {
  const {
    value: selectedImages = [],
    images,
    onFieldChange,
    field,
    max = images.length,
    translations,
    label,
    valid,
    imageProperty,
  } = props;

  const { t } = useTranslation('content_policy_reporting');

  const [isBlur, setIsBlur] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  const isSelectedImage = (image: T) => {
    if (typeof image !== 'string') {
      return Boolean(selectedImages.find(selectedImage => selectedImage[imageProperty] === image[imageProperty]));
    }

    return selectedImages.includes(image);
  };

  const toggleImage = (image: T) => {
    if (max === 1) {
      onFieldChange({
        fieldIdentifier: field.name,
        value: isSelectedImage(image) ? [] : [image],
        valid: true,
      });
      return;
    }

    const newSelectedImages = isSelectedImage(image)
      ? filter(selectedImages, negate(partial(isEqual, image)))
      : [...selectedImages, image];

    const isFieldValid = field.required ? newSelectedImages.length > 0 && newSelectedImages.length <= max : true;

    onFieldChange({
      fieldIdentifier: field.name,
      value: newSelectedImages,
      valid: isFieldValid,
    });
  };

  const getError = () => {
    return (
      <FormHelpText data-testid="validation-error" error>
        {translations.errorMessage}
      </FormHelpText>
    );
  };

  const { pageContent: imagesToDisplay, totalPages } = paginate(images, IMAGES_PER_PAGE, page);

  return (
    <>
      <Type variant="balladBold">
        {label}
        {field.required && <span className={styles.required}>*</span>}
      </Type>
      {!!translations.disclaimer && <Banner colorSet="announcement">{translations.disclaimer}</Banner>}
      {!valid && getError()}

      <ul className={styles.images} role="listbox" aria-multiselectable="true">
        {imagesToDisplay.map((image: T, index: number) => {
          const classname = [styles.image, isSelectedImage(image) ? styles.selected : ''].join(' ');
          const imageSrc = typeof image !== 'string' ? (image[imageProperty] as unknown as string) : image;

          return (
            <li
              role="option"
              aria-selected={isSelectedImage(image)}
              tabIndex={index}
              className={classname}
              key={index}
              onClick={() => toggleImage(image)}
              onKeyDown={() => toggleImage(image)}
            >
              <Image
                src={imageSrc}
                alt={label}
                imageHeight="150px"
                imageWidth="150px"
                data-testid={imageSrc}
                crop
                className={isBlur ? styles.blur : ''}
              />
            </li>
          );
        })}
      </ul>

      <div className={styles.bottomMenu}>
        <FormCheckbox id="blur-warning" defaultChecked isActive={isBlur} onChange={() => setIsBlur(!isBlur)}>
          <span
            dangerouslySetInnerHTML={{
              __html: t('image_unblur_warning'),
            }}
          />
        </FormCheckbox>
        {totalPages > 1 && (
          <div className={styles.paginationContainer} data-testid="pagination">
            <PaginationControls
              onIncrement={page < totalPages ? () => setPage(clamp(page + 1, 1, totalPages)) : undefined}
              onDecrement={page > 1 ? () => setPage(clamp(page - 1, 1, totalPages)) : undefined}
            >
              {page} {translations.pagination} {totalPages}
            </PaginationControls>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageChooser;
